import { Node } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import { useQuery } from "@apollo/client";
import { useEffect } from "react";
import { GET_VIDEO } from "../../../../../utils/api/video";
import { isVideoReady, isVideoFailed } from "../../../../../utils/video-utils";
import Spinner from "../../../../icons/Spinner";

interface VideoOptions {
  inline: boolean;
  HTMLAttributes: Record<string, any>;
}

interface VideoAttributes {
  libraryId: string;
  videoId: string;
  videoDbId?: string;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    video: {
      insertVideo: (attrs: VideoAttributes) => ReturnType;
    };
  }
}

const BUNNY_LIBRARY_ID = process.env.REACT_APP_BUNNY_LIBRARY_ID;
const VIDEO_WIDTH = 640;
const VIDEO_HEIGHT = 360;

const VideoNodeView = ({ node }: { node: any }) => {
  const { libraryId, videoId, videoDbId } = node.attrs;

  const { data, error, stopPolling } = useQuery(GET_VIDEO, {
    variables: { id: videoDbId },
    skip: !videoDbId,
    pollInterval: 5000,
  });

  const videoStatus = data?.video?.status;
  const isReady = isVideoReady(videoStatus);
  const isFailed = isVideoFailed(videoStatus) || !!error;
  const isProcessing = videoDbId && !isReady && !isFailed;

  useEffect(() => {
    if (isReady || isFailed) {
      stopPolling();
    }
  }, [isReady, isFailed, stopPolling]);

  if (isFailed) {
    return (
      <NodeViewWrapper
        className='processing-video flex flex-col items-center justify-center bg-gray-100 rounded-sm mx-auto'
        style={{width: VIDEO_WIDTH, height: VIDEO_HEIGHT}}
      >
        <p className='text-red-600 text-md'>Video failed to process.</p>
        <p className='text-gray-500 text-sm'>Please remove it and upload again.</p>
      </NodeViewWrapper>
    )
  }

  if (isProcessing) {
    return (
      <NodeViewWrapper
        className='processing-video flex flex-col items-center justify-center bg-gray-100 rounded-sm mx-auto'
        style={{width: VIDEO_WIDTH, height: VIDEO_HEIGHT}}
      >
        <Spinner />
        <p className='mt-4 text-gray-500 text-md'>Processing video...</p>
        <p className='text-gray-500 text-sm'>This usually takes a few minutes</p>
      </NodeViewWrapper>
    )
  }

  return (
    <NodeViewWrapper className="text-center">
      <iframe
        title="Video player"
        src={`https://iframe.mediadelivery.net/embed/${libraryId}/${videoId}`}
        loading="lazy"
        style={{border: 'none', width: VIDEO_WIDTH, height: VIDEO_HEIGHT, margin: 'auto' }}
        allow="accelerometer; gyroscope; encrypted-media; picture-in-picture;"
        allowFullScreen
      />
    </NodeViewWrapper>
  );
};

const Video = Node.create<VideoOptions>({
  name: "video",

  addNodeView() {
    return ReactNodeViewRenderer(VideoNodeView);
  },

  addOptions() {
    return {
      inline: false,
      HTMLAttributes: {},
    };
  },

  inline() {
    return this.options.inline;
  },

  group() {
    return this.options.inline ? "inline" : "block";
  },

  draggable: true,

  addAttributes() {
    return {
      libraryId: {
        default: BUNNY_LIBRARY_ID,
      },
      videoId: {
        default: null,
      },
      videoDbId: {
        default: null,
      },
      interscroller: {
        default: false,
      },
    };
  },

  renderHTML: ({ HTMLAttributes: { libraryId, videoId } }) => {
    return [
      "div",
      { style: "text-align: center;" },
      [
        "iframe",
        {
          src: `https://iframe.mediadelivery.net/embed/${libraryId}/${videoId}`,
          loading: "lazy",
          style: `border: none; width: ${VIDEO_WIDTH}px; height: ${VIDEO_HEIGHT}px;`,
          allow: "accelerometer; gyroscope; encrypted-media; picture-in-picture;",
          allowFullScreen: "true",
        },
      ],
    ];
  },

  addCommands() {
    return {
      insertVideo:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({ type: this.name, attrs });
        },
    };
  },
});

export default Video;
